import React, { useState, useEffect, useCallback } from 'react';
import { Feature } from "models/api";
import { QueryClient, useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import { addEmployeeAssessment, getWorkshopAssessment } from 'framework/employeeWorkshopAssessment';
import { IEmployeeWorkshopAssessmentDetails } from 'models/employeeWorkshopAssessmentDetails';
import { Button, Card, Grid, Typography } from '@mui/material';

interface Props {
  feature: Feature;
}

const EmployeeWorkshopAssessmentDetails: React.FC<Props> = ({ feature }) => {
  const [assessmentDetails, setAssessmentDetails] = useState<IEmployeeWorkshopAssessmentDetails>({} as IEmployeeWorkshopAssessmentDetails);

  const [loading, setLoading] = useState<boolean>(true);
  const queryClient = new QueryClient();
  const urlParams = useParams();
  const { setAlert } = useAlert();
  let id: string = urlParams[`${feature.singleName}Id`] as string;

  const addEmployeeWorkshopAssessmentMutation = useMutation({
    mutationFn: () => addEmployeeAssessment(id),
    onSuccess: (data) => {
      navigate(`/employeeWorkshopAssessment/${id}`);
    }
  });

  const navigate = useNavigate();

  const handleButtonClick = async () => {
    addEmployeeWorkshopAssessmentMutation.mutate();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await queryClient.fetchQuery(
        "get Workshop Assessment Details",
        () => getWorkshopAssessment("/foremployee?id=" + id)
      );
      setAssessmentDetails(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
  }, []);


  if (loading) return <p>Loading Details...</p>;


  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 0 }}
        >
          Back
        </Button>
      </div>
      <div style={{ margin: 'auto' }}>
        <Card style={{ padding: '20px', marginBottom: '20px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" component="div" gutterBottom>
                Workshop Assessment Details
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Workshop Title (EN):</Typography>
              <Typography variant="body1">{assessmentDetails.workshopTitle}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Workshop Title (AR):</Typography>
              <Typography variant="body1">{assessmentDetails.workshopTitleAr}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Trainer Name (EN):</Typography>
              <Typography variant="body1">{assessmentDetails.trainerName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Trainer Name (AR):</Typography>
              <Typography variant="body1">{assessmentDetails.trainerNameAr}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Workshop Date:</Typography>
              <Typography variant="body1">{assessmentDetails.workshopDate}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Assessment Title (EN):</Typography>
              <Typography variant="body1">{assessmentDetails.assessmentTitle}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Assessment Title (AR):</Typography>
              <Typography variant="body1">{assessmentDetails.assessmentTitleAr}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Assessment Type (EN):</Typography>
              <Typography variant="body1">{assessmentDetails.assessmentType}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Assessment Type (AR):</Typography>
              <Typography variant="body1">{assessmentDetails.assessmentTypeAr}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Number of Questions:</Typography>
              <Typography variant="body1">{assessmentDetails.numberOfQuestions}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Time to Complete (min):</Typography>
              <Typography variant="body1">{assessmentDetails.timeToComplete / 60}</Typography>
            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: '20px' }}>
              {!assessmentDetails.completed ? (
                <Button variant="contained" color="primary" onClick={handleButtonClick}>
                  Proceed
                </Button>
              ) : (
                <h4>You have completed your assessment</h4>
              )}
            </Grid>
          </Grid>
        </Card>
      </div>

    </div>
  );
};

export default EmployeeWorkshopAssessmentDetails;
